import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from './context';

export type InvestmentsTab = 'portfolio' | 'performance' | 'movements' | 'market';

export type InvestmentsSection =
  | 'evolution'
  | 'performance'
  | 'monthly_profit'
  | 'monthly_performance'
  | 'rescue_index'
  | 'investment_calculator'
  | 'by_due'
  | 'by_type'
  | 'low_performance'
  | 'table_detail';

export type InvestmentsConfigKey =
  | 'dataType'
  | 'dateType'
  | 'year'
  | 'instrumentType';

type TrackFn = (eventName: string, data: Record<string, unknown>) => void;

type InvestmentsTracker = {
  portfolioDrilled: (params: {
    instrumentType: string;
    source?: 'overview_card' | 'type_list' | 'url';
  }) => void;

  viewConfigChanged: (params: {
    tab: InvestmentsTab;
    section: InvestmentsSection;
    configKey: InvestmentsConfigKey;
    value: string | number | null | undefined;
    previousValue?: string | number | null;
  }) => void;

  periodSelected: (params: {
    tab: InvestmentsTab;
    section: InvestmentsSection;
    dateFrom: string | null;
    dateTo: string | null;
    rangeDays?: number;
    preset?: string;
  }) => void;

  applicationsGrouped: (params: {
    instrumentType?: string | null;
    groupBy: string;
    previousGroupBy: string;
  }) => void;

  applicationDetailOpened: (params: {
    applicationId: string;
    instrumentType?: string | null;
    openedFrom: 'table' | 'expand_detail' | 'movements';
  }) => void;

  cdiPercentageEdited: (params: {
    applicationId: string;
    instrumentType?: string | null;
    previousValue?: number | null;
    newValue: number | null;
  }) => void;

  calculatorUsed: (params: {
    amount?: number | null;
    redemptionTerm?: number | null;
    instrumentTypeFrom?: string | null;
    instrumentTypeTo?: string | null;
  }) => void;

  rescueIndexFiltered: (params: {
    instrumentType?: string | null;
    month?: string | null;
  }) => void;

  maturityNavigated: (params: {
    direction: 'next_day' | 'previous_day' | 'next_month' | 'previous_month' | 'select_date';
    instrumentType?: string | null;
    itemCount?: number | null;
  }) => void;

  lowPerformanceConfigSaved: (params: {
    instrumentType?: string | null;
    cdiDeposit?: number;
    cdiFund?: number;
    cdiCommitted?: number;
    cdiCoe?: number;
    committedDays?: number;
  }) => void;

  manualApplicationSaved: (params: {
    applicationId?: string | null;
    instrumentType?: string | null;
    remunerationIndex?: string | null;
    isEdit: boolean;
  }) => void;

  manualApplicationDeleted: (params: {
    applicationId: string;
    instrumentType?: string | null;
  }) => void;

  movementDetailOpened: (params: {
    movementId?: string | null;
    direction?: string | null;
    instrumentType?: string | null;
    surface: 'row_actions' | 'asset_link';
  }) => void;
};

export const useInvestmentsTracking = (): InvestmentsTracker => {
  const analytics = useAnalyticsContext();

  const track: TrackFn = useCallback((eventName, data) => {
    try {
      analytics?.mixPanel?.trackContinueEvent?.({
        customEvent: eventName,
        module: 'investments',
        ...data,
      });
    } catch {
      // Tracking must never break the UI.
    }
  }, [analytics]);

  return useMemo<InvestmentsTracker>(() => ({
    portfolioDrilled: (params) => track('investments_portfolio_drilled', params),
    viewConfigChanged: (params) => track('investments_view_config_changed', params),
    periodSelected: (params) => track('investments_period_selected', params),
    applicationsGrouped: (params) => track('investments_applications_grouped', params),
    applicationDetailOpened: (params) => track('investments_application_detail_opened', params),
    cdiPercentageEdited: (params) => track('investments_cdi_percentage_edited', params),
    calculatorUsed: (params) => track('investments_calculator_used', params),
    rescueIndexFiltered: (params) => track('investments_rescue_index_filtered', params),
    maturityNavigated: (params) => track('investments_maturity_navigated', params),
    lowPerformanceConfigSaved: (params) => track('investments_low_performance_config_saved', params),
    manualApplicationSaved: (params) => track('investments_manual_application_saved', params),
    manualApplicationDeleted: (params) => track('investments_manual_application_deleted', params),
    movementDetailOpened: (params) => track('investments_movement_detail_opened', params),
  }), [track]);
};
